import classNames from "classnames";
import { useRef, useState } from "react";
import { useEffectOnce, useOnClickOutside } from "usehooks-ts";
import { TextField } from "components";
import { Option } from "./Option";
import styles from "./Select.module.css";

type OptionType = string | number;

type SelectProps = {
  name: string;
  label: string;
  placeholder?: string;
  options: OptionType[];
  defaults?: OptionType[];
  position?: "up" | "down";
  clearLabel?: string;
  multiple?: boolean;
};

const Select: React.FC<SelectProps> = ({
  name,
  label,
  placeholder,
  options,
  defaults = [],
  position = "down",
  clearLabel,
  multiple,
}) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<OptionType[]>([]);

  const handleReset = () => setSelected([]);

  useOnClickOutside(ref, () => {
    if (open) {
      setOpen(false);
    }
  });

  useEffectOnce(() => {
    setSelected(defaults);
  });

  return (
    <div
      ref={ref}
      className={classNames(
        styles.container,
        open && styles.open,
        styles[position]
      )}
      onClick={() => setOpen(true)}
    >
      <TextField
        name={name}
        label={label}
        placeholder={placeholder}
        defaultValue={selected.join(", ")}
        iconEnd={open ? "expand_less" : "expand_more"}
        readOnly
      />
      {open && (
        <div className={styles.optionsContainer}>
          {options.map((option) => {
            const isActive = selected.includes(option);

            const handleClick = () => {
              if (isActive) {
                setSelected((old) => {
                  if (multiple) {
                    return old.filter((active) => active !== option);
                  } else {
                    return old;
                  }
                });
              } else {
                setSelected((old) => {
                  if (multiple) {
                    return [...old, option];
                  } else {
                    return [option];
                  }
                });
              }
            };

            return (
              <Option key={option} active={isActive} onClick={handleClick}>
                {option}
              </Option>
            );
          })}
          {clearLabel && <Option onClick={handleReset}>{clearLabel}</Option>}
        </div>
      )}
    </div>
  );
};

export { Select };
